'use client';

import { cn, Avatar, AvatarImage, AvatarFallback } from './_adapter';
import { Heart, MessageCircle, Send, Bookmark, MoreHorizontal } from 'lucide-react';

interface InstagramPostProps {
  authorName: string;
  authorAvatarUrl?: string;
  verified?: boolean;
  content: string;
  media?: React.ReactNode;
  className?: string;
}

export function InstagramPost({
  authorName,
  authorAvatarUrl,
  verified = false,
  content,
  media,
  className,
}: InstagramPostProps) {
  const initials = authorName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className={cn('bg-white dark:bg-zinc-900 rounded-lg border max-w-lg', className)}>
      {/* Header */}
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-3">
          <div className="p-0.5 rounded-full bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-600">
            <Avatar className="h-8 w-8 border-2 border-white dark:border-zinc-900">
              {authorAvatarUrl ? (
                <AvatarImage src={authorAvatarUrl} alt={authorName} />
              ) : null}
              <AvatarFallback className="bg-zinc-200 dark:bg-zinc-700 text-xs font-medium">
                {initials}
              </AvatarFallback>
            </Avatar>
          </div>
          <div className="flex items-center gap-1">
            <span className="font-semibold text-sm text-foreground">{authorName}</span>
            {verified && <VerifiedBadge />}
          </div>
        </div>
        <button className="p-1 hover:opacity-70 transition-opacity">
          <MoreHorizontal className="w-5 h-5 text-foreground" />
        </button>
      </div>

      {/* Media */}
      {media && <div className="aspect-square bg-muted">{media}</div>}

      {/* Actions */}
      <div className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-4">
            <ActionButton icon={Heart} />
            <ActionButton icon={MessageCircle} />
            <ActionButton icon={Send} />
          </div>
          <ActionButton icon={Bookmark} />
        </div>

        {/* Likes */}
        <p className="font-semibold text-sm text-foreground mb-1">0 likes</p>

        {/* Caption */}
        <p className="text-sm text-foreground">
          <span className="font-semibold">{authorName}</span>{' '}
          <span className="whitespace-pre-wrap">{content}</span>
        </p>

        {/* Timestamp */}
        <p className="text-xs text-muted-foreground mt-2 uppercase">Just now</p>
      </div>
    </div>
  );
}

function ActionButton({ icon: Icon }: { icon: React.ComponentType<{ className?: string }> }) {
  return (
    <button className="hover:opacity-60 transition-opacity">
      <Icon className="w-6 h-6 text-foreground" />
    </button>
  );
}

function VerifiedBadge() {
  return (
    <svg
      viewBox="0 0 40 40"
      className="w-4 h-4 text-blue-500"
      fill="currentColor"
      aria-label="Verified"
    >
      <path d="M19.998 3.094L14.638 0l-2.972 5.15H5.432v6.354L0 14.64 3.094 20 0 25.359l5.432 3.137v5.905h5.975L14.638 40l5.36-3.094L25.358 40l3.232-5.6h6.162v-6.01L40 25.359 36.905 20 40 14.641l-5.248-3.03v-6.46h-6.419L25.358 0l-5.36 3.094zm7.415 11.225l2.254 2.287-11.43 11.5-6.835-6.93 2.244-2.258 4.587 4.581 9.18-9.18z" />
    </svg>
  );
}
